import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
  Card,
  CardContent,
} from "@mui/material";
import { PhotoCamera, Upload } from "@mui/icons-material";
import axios from "axios";
import "./CropClinic.css";
import BASE_URL from "../../../config/apiConfig";

const CropClinic = () => {
  const { crop_name } = useParams();
  const location = useLocation();
  const { id, crop_clinic, cropname } = location.state || {};
  const [cropName] = useState(crop_name);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disease, setDisease] = useState(null);
  const [precautions, setPrecautions] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);

  const fetchRecommendedProducts = async (diseaseName, cropName) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/crop-clinic/recommended-products`,
        {
          diseaseName,
          cropName,
        }
      );
      setRecommendedProducts(response.data.products || []);
    } catch (error) {
      console.error("Error fetching recommended products:", error);
    }
  };

  useEffect(() => {
    if (disease && cropName) {
      fetchRecommendedProducts(disease, cropName);
    }
  }, [disease]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      setOpen(false);
      setDisease(null);
      setPrecautions(null);
    }
  };

  const handleDiagnose = async () => {
    if (!selectedImage) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("image", selectedImage);
    formData.append("crop_name", cropname);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/crop-clinic/identify`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const diseaseName = response.data.diseaseName;
      const imageName = response.data.imageName;

      setDisease(diseaseName);

      const precautionsResponse = await axios.post(
        `${BASE_URL}/api/user/crop-clinic/precautions`,
        {
          diseaseName,
          id,
          crop_clinic,
          cropname,
          imageName,
        }
      );

      setPrecautions(precautionsResponse.data.precautions);
    } catch (error) {
      console.error("Error:", error);
      setDisease("Error identifying disease.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clinic-container">
      {/* Title */}
      <Typography variant="h4" className="title">
        Welcome to Crop Clinic
      </Typography>

      {/* Intro Section */}
      <Card className="intro-card">
        <CardContent>
          <Typography variant="h6" className="intro-text">
            Diagnose your crop's health instantly! Upload a clear image of your
            crop, and our system will analyze its health and provide actionable
            insights to keep your plants thriving.
          </Typography>
          <div className="card-sub-content">
            <div className="left-section">
              <Typography variant="h6" className="instructions-title">
                Photo Submission Instructions:
              </Typography>
              <Typography variant="body1" className="intro-instructions">
                <ul>
                  <li>Ensure good lighting when taking the photo.</li>
                  <li>Focus on the affected area (leaves, stem, or fruit).</li>
                  <li>Avoid blurry or distant images.</li>
                  <li>
                    Upload images smaller than 5 MB for faster processing.
                  </li>
                </ul>
              </Typography>
            </div>
            <div className="right-section">
              <Typography variant="h6" className="tips-title">
                Tips for Best Results:
              </Typography>

              <div className="tips-container">
                <div className="tip">
                  <img
                    src="/Images/goodImage.webp"
                    alt="Good quality"
                    className="tip-image"
                  />
                  <Typography variant="body2" className="tip-text">
                    Good: Clear, focused, and well-lit image.
                  </Typography>
                </div>
                <div className="tip">
                  <img
                    src="/Images/blurrImage.webp"
                    alt="Bad quality"
                    className="tip-image"
                  />
                  <Typography variant="body2" className="tip-text">
                    Bad: Blurry or overexposed image.
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Crop Input & Upload Section */}
      <TextField
        label="Crop Name"
        value={cropName}
        variant="outlined"
        InputProps={{
          readOnly: true,
        }}
        className="crop-input"
      />

      {/* Photo Upload */}
      <div className="photo-section">
        <Button
          variant="contained"
          onClick={handleOpen}
          startIcon={<Upload />}
          className="upload-button"
        >
          Upload or Take Photo
        </Button>
        {selectedImage && (
          <div className="preview">
            <img
              src={URL.createObjectURL(selectedImage)}
              alt="Selected crop"
              className="preview-image"
            />
          </div>
        )}
      </div>

      {/* Diagnose Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleDiagnose}
        className="diagnose-button"
        disabled={!selectedImage || loading}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Diagnose Your Crop"
        )}
      </Button>

      {/* Results */}
      {disease && (
        <Box className="result-container">
          <Typography variant="h6" className="result-title">
            Diagnosis:
          </Typography>
          <Typography variant="body1" className="result-content">
            {disease}
          </Typography>

          {precautions && (
            <>
              <Typography variant="h6" className="result-precautions-title">
                Precautions:
              </Typography>
              <Typography variant="body1" className="result-content">
                {precautions}
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Dialog for Photo Upload */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Upload or Take Photo</DialogTitle>
        <DialogContent>
          <div className="dialog-options">
            <Button
              variant="contained"
              component="label"
              startIcon={<PhotoCamera />}
              className="dialog-button"
            >
              Take Photo
              <input
                hidden
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
              />
            </Button>
            <Button
              variant="contained"
              component="label"
              startIcon={<Upload />}
              className="dialog-button"
            >
              Upload from Gallery
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
              />
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recommended Products Section */}
      {recommendedProducts.length > 0 && (
        <Box className="recommendation-container">
          <Typography variant="h6" className="recommendation-title">
            Recommended Products:
          </Typography>
          <div className="products-grid">
            {recommendedProducts.map((product) => (
              <Card key={product.id} className="product-card">
                <CardContent>
                  <img
                    src={`${BASE_URL}/media/${product.product_image}`}
                    alt={product.product_name}
                    className="product-image"
                  />
                  <Typography variant="h6" className="product-name">
                    {product.product_name}
                  </Typography>
                  <Typography variant="body2" className="product-description">
                    {product.product_description}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    href={product.product_link}
                    target="_blank"
                    className="product-link"
                  >
                    Buy Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </Box>
      )}
    </div>
  );
};

export default CropClinic;
